import * as api from '@api/iyagigo/api-function';
import { EQUERY_KEY } from '@constant/iyagigo/query-key';
import {
  useApiQuery,
  useApiInfiniteQuery,
  useApiMutation,
  appendQueryDataItem,
  removeQueryDataItem,
  replaceQueryDataItem,
  mergeQueryDataItem,
  replaceInfiniteQueryDataItem,
  mergeInfiniteQueryDataItem,
  snapshotQueryData,
  rollbackQueryData,
} from '@hook/common/useApi';
import { queryClient } from '@config/query-client';
import { useTypedSelector } from '@hook/common/useStore';
import {
  EApiAttendanceType,
  EApiLanguageCode,
  EApiStoryType,
  EApiVocabularyBookCategory,
  EApiVocabularyBookLevel,
  IAttendanceData,
  IAuthenticExpressionData,
  ICheckAttendanceRequest,
  IModifyProblemRequest,
  IModifyStudentRequest,
  IModifyWorkbookRequest,
  IModifyWorksheetWorkbookRequest,
  IWorksheetWorkbookData,
  IProblemData,
  IStoryData,
  IStudentData,
  ISpeakingSessionData,
  IWorkbookData,
  TClozeType,
  TTestCode,
  EApiCharacterType,
  ICreateStudentRequest,
  IClassroomCharacterGroupWorkbookData,
  IModifyCharacterGroupWorkbookRequest,
  IClassroomGrammarWorkbookData,
  IModifyClassroomGrammarWorkbookRequest,
  IClassroomExpressionWorkbookData,
  IModifyClassroomExpressionWorkbookRequest,
  IClassroomConversationWorkbookData,
  IModifyClassroomConversationWorkbookRequest,
  ICreateStudyRecordRequest,
  ICreateSpeakingSessionRequest,
} from '@datatype/iyagigo/api-type';
import { IApiResponse, TAudioFormat } from '@datatype/common/api-type';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { INFINITE_SCROLL_PER_PAGE } from '@constant/iyagigo/variable';
import { getTodayDateKey } from '@util/common';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { ESERVER_ERROR_CODE } from '@datatype/enum';
import {
  IModifyVocabularyWorkbookRequest,
  IVocabularyWorkbookData,
  IReadVocabularyBooksRequest,
  IReadVocabularyBookProgressRequest,
} from '@datatype/iyagigo/api-type';

// /**
//  * category 목록 query 함수
//  */
// export const useCategoriesQuery = (page: number, perPage: number) => {
//   const queryKey = [EQUERY_KEY.CATEGORIES];
//   return useApiQuery(queryKey, api.getCategories, { page, perPage });
// };

// ############### problem ###############
export const useModifyProblemMutation = () => {
  return useApiMutation<IApiResponse<IProblemData>, IModifyProblemRequest>(api.modifyProblem, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;

      // replaceQueryDataItem([EQUERY_KEY.MY_SHELF], itemKey, updatedItem);
    },
  });
};

// ############### character ###############
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// 백엔드 /classroom-* 목록 엔드포인트도 @Deprecated. 전환 확인 후 함께 삭제 예정.
// export const useCharacterGroupsQuery = (languageCode: EApiLanguageCode, characterType: EApiCharacterType) => {
//   const queryKey = [EQUERY_KEY.CLASSROOM_CHARACTER_GROUPS, languageCode, characterType];
//   return useApiQuery(queryKey, api.getCharacterGroups, { languageCode, characterType });
// };

export const useCharacterGroupWorkbooksQuery = (languageCode: EApiLanguageCode, characterType: EApiCharacterType) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.CLASSROOM_CHARACTER_GROUP_WORKBOOKS, languageCode, characterType, studentKey];
  return useApiQuery(
    queryKey,
    api.getCharacterGroupWorkbooks,
    { languageCode, characterType, studentKey },
    { enabled: !!studentKey },
  );
};

export const useModifyCharacterGroupWorkbookMutation = (
  languageCode: EApiLanguageCode,
  characterType: EApiCharacterType,
) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  return useApiMutation<IApiResponse<IClassroomCharacterGroupWorkbookData>, IModifyCharacterGroupWorkbookRequest>(
    api.modifyCharacterGroupWorkbook,
    {
      onSuccess: (data) => {
        const itemKey = data.data.key;
        const updatedItem = data.data;
        replaceQueryDataItem([EQUERY_KEY.CLASSROOM_CHARACTER_GROUP_WORKBOOKS, languageCode, characterType, studentKey], undefined, itemKey, updatedItem);
      },
    },
  );
};

// ############### classroom grammar ###############
export const useSpeakingTasksQuery = (languageCode: EApiLanguageCode) => {
  const queryKey = [EQUERY_KEY.SPEAKING_TASKS, languageCode];
  return useApiQuery(queryKey, api.getSpeakingTasks, { languageCode });
};

export const useCreateSpeakingSessionMutation = () => {
  const dispatch = useTypedDispatch();

  return useApiMutation<IApiResponse<ISpeakingSessionData>, ICreateSpeakingSessionRequest>(api.createSpeakingSession, {
    onError: (error) => {
      if (error.response?.data?.code !== ESERVER_ERROR_CODE.CLIENT_0005) return;
      dispatch(modalActions.showWallToast({ type: 'subscription-wall', data: { trigger: 'quota', feature: 'speaking' } }));
    },
  });
};

export const useSpeakingSessionQuery = (sessionKey: string) => {
  return useApiQuery([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], api.getSpeakingSession, sessionKey);
};

export const useSpeakingTurnTranscribeMutation = (sessionKey: string) => {
  return useApiMutation((turnKey: string) => api.createSpeakingTurnTranscribe(sessionKey, turnKey), {
    onSuccess: ({ data }) => {
      replaceQueryDataItem([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], 'speakingTurns', data.key, data);
    },
  });
};

export const useCreateSpeakingTurnMutation = (sessionKey: string) => {
  return useApiMutation(() => api.createSpeakingTurn(sessionKey), {
    onSuccess: ({ data }) => {
      appendQueryDataItem([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], 'speakingTurns', data.key, data, 'end');
    },
  });
};

export const useSpeakingTurnUploadMutation = (sessionKey: string) => {
  return useApiMutation(({ turnKey, format }: { turnKey: string; format: TAudioFormat }) =>
    api.createSpeakingTurnUpload(sessionKey, turnKey, format),
  );
};

export const useCheckSpeakingTurnUploadMutation = (sessionKey: string) => {
  return useApiMutation((turnKey: string) => api.checkSpeakingTurnUpload(sessionKey, turnKey), {
    onSuccess: ({ data }) => {
      replaceQueryDataItem([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], 'speakingTurns', data.key, data);
    },
  });
};

export const useSpeakingTurnAnalyzeMutation = (sessionKey: string) => {
  return useApiMutation((turnKey: string) => api.createSpeakingTurnAnalyze(sessionKey, turnKey), {
    onSuccess: ({ data }) => {
      replaceQueryDataItem([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], 'speakingTurns', data.key, data);
    },
  });
};

export const useSpeakingTurnRespondMutation = (sessionKey: string) => {
  return useApiMutation((turnKey: string) => api.createSpeakingTurnRespond(sessionKey, turnKey), {
    onSuccess: ({ data }) => {
      if (!data) return;
      const queryKey = [EQUERY_KEY.SPEAKING_SESSIONS, sessionKey];
      appendQueryDataItem(queryKey, 'speakingTurns', data.key, data, 'end');
      if (data.sessionCompletedAt) {
        mergeQueryDataItem<ISpeakingSessionData>(queryKey, sessionKey, { completedAt: data.sessionCompletedAt });
      }
    },
  });
};

export const useRemoveSpeakingTurnMutation = (sessionKey: string) => {
  return useApiMutation((turnKey: string) => api.removeSpeakingTurn(sessionKey, turnKey), {
    onSuccess: (_, turnKey) => {
      removeQueryDataItem([EQUERY_KEY.SPEAKING_SESSIONS, sessionKey], 'speakingTurns', turnKey);
    },
  });
};

// 지난 대화 목록. 한 번이라도 말한 세션만 최신순으로 온다.
export const useSpeakingSessionPageQuery = (studentKey: string, languageCode: EApiLanguageCode) => {
  const queryKey = [EQUERY_KEY.SPEAKING_SESSIONS, studentKey, languageCode];
  return useApiInfiniteQuery<
    ISpeakingSessionData,
    { studentKey: string; languageCode: EApiLanguageCode; page: number; perPage: number }
  >(
    queryKey,
    api.getSpeakingSessionPage,
    { studentKey, languageCode, perPage: INFINITE_SCROLL_PER_PAGE },
    { enabled: !!studentKey },
  );
};

// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// 백엔드 /classroom-* 목록 엔드포인트도 @Deprecated. 전환 확인 후 함께 삭제 예정.
// export const useClassroomGrammarsQuery = (languageCode: EApiLanguageCode) => {
//   const queryKey = [EQUERY_KEY.CLASSROOM_GRAMMARS, languageCode];
//   return useApiQuery(queryKey, api.getClassroomGrammars, { languageCode });
// };

export const useClassroomGrammarWorkbooksQuery = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.CLASSROOM_GRAMMAR_WORKBOOKS, languageCode, studentKey];
  return useApiQuery(
    queryKey,
    api.getClassroomGrammarWorkbooks,
    { languageCode, studentKey },
    { enabled: !!studentKey },
  );
};

export const useModifyClassroomGrammarWorkbookMutation = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  return useApiMutation<IApiResponse<IClassroomGrammarWorkbookData>, IModifyClassroomGrammarWorkbookRequest>(
    api.modifyClassroomGrammarWorkbook,
    {
      onSuccess: (data) => {
        replaceQueryDataItem([EQUERY_KEY.CLASSROOM_GRAMMAR_WORKBOOKS, languageCode, studentKey], undefined, data.data.key, data.data);
      },
    },
  );
};

// ############### basic expression ###############
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// 백엔드 /classroom-* 목록 엔드포인트도 @Deprecated. 전환 확인 후 함께 삭제 예정.
// export const useClassroomExpressionsQuery = (languageCode: EApiLanguageCode) => {
//   const queryKey = [EQUERY_KEY.CLASSROOM_EXPRESSIONS, languageCode];
//   return useApiQuery(queryKey, api.getClassroomExpressions, { languageCode });
// };

export const useClassroomExpressionWorkbooksQuery = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.CLASSROOM_EXPRESSION_WORKBOOKS, languageCode, studentKey];
  return useApiQuery(
    queryKey,
    api.getClassroomExpressionWorkbooks,
    { languageCode, studentKey },
    { enabled: !!studentKey },
  );
};

export const useModifyClassroomExpressionWorkbookMutation = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  return useApiMutation<IApiResponse<IClassroomExpressionWorkbookData>, IModifyClassroomExpressionWorkbookRequest>(
    api.modifyClassroomExpressionWorkbook,
    {
      onSuccess: (data) => {
        replaceQueryDataItem([EQUERY_KEY.CLASSROOM_EXPRESSION_WORKBOOKS, languageCode, studentKey], undefined, data.data.key, data.data);
      },
    },
  );
};

// ############### practical conversation ###############
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// 백엔드 /classroom-* 목록 엔드포인트도 @Deprecated. 전환 확인 후 함께 삭제 예정.
// export const useClassroomConversationsQuery = (languageCode: EApiLanguageCode) => {
//   const queryKey = [EQUERY_KEY.CLASSROOM_CONVERSATIONS, languageCode];
//   return useApiQuery(queryKey, api.getClassroomConversations, { languageCode });
// };

export const useClassroomConversationWorkbooksQuery = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.CLASSROOM_CONVERSATION_WORKBOOKS, languageCode, studentKey];
  return useApiQuery(
    queryKey,
    api.getClassroomConversationWorkbooks,
    { languageCode, studentKey },
    { enabled: !!studentKey },
  );
};

export const useModifyClassroomConversationWorkbookMutation = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  return useApiMutation<IApiResponse<IClassroomConversationWorkbookData>, IModifyClassroomConversationWorkbookRequest>(
    api.modifyClassroomConversationWorkbook,
    {
      onSuccess: (data) => {
        replaceQueryDataItem([EQUERY_KEY.CLASSROOM_CONVERSATION_WORKBOOKS, languageCode, studentKey], undefined, data.data.key, data.data);
      },
    },
  );
};

// problem 목록 query 함수
export const useProblemsQuery = (testCode: string | null, page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.PROBLEMS, testCode ?? ''];
  return useApiQuery(queryKey, api.getProblems, { page, perPage, testCode: testCode });
};

// ############### bundle ###############
// bundle(problem 모음) 목록 query 함수
export const useBundlesQuery = (bundleCode: string, bundleDate: string, page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.BUNDLES, bundleCode ?? '', bundleDate];
  return useApiQuery(queryKey, api.getBundles, { page, perPage, bundleCode, bundleDate }, { enabled: !!bundleCode });
};

// bundle 날짜 목록 query 함수
export const useBundleDatesQuery = (page: number, perPage: number) => {
  const queryKey = [EQUERY_KEY.BUNDLE_DATES ?? '', new Date().getDate().toString()];
  return useApiQuery(queryKey, api.getBundleDates, { page, perPage });
};

// ############### student ###############
// student(me) 단일 query 함수
export const useStudentMeQuery = (options?: { enabled?: boolean }) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [EQUERY_KEY.STUDENT_ME, studentKey];
  return useApiQuery(queryKey, api.getStudentMe, {}, { enabled: options?.enabled ?? false });
};

// student 단일 query 함수
export const useStudentKeyQuery = (options?: { enabled?: boolean }) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [EQUERY_KEY.STUDENT, studentKey];
  return useApiQuery(queryKey, api.getStudent, studentKey, {
    enabled: (options?.enabled ?? true) && !!studentKey,
    // staleTime: 60 * 60 * 1000,
  });
};

// student 생성 mutation 함수
export const useCreateStudentMutation = () => {
  return useApiMutation<IApiResponse<IStudentData>, ICreateStudentRequest>(api.createStudent);
};

// student(studentKey) 수정 mutation 함수
export const useModifyStudentMutation = () => {
  return useApiMutation<IApiResponse<IStudentData>, IModifyStudentRequest>(api.modifyStudent, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([EQUERY_KEY.STUDENT, itemKey], undefined, itemKey, updatedItem);
      // #### 학습설정(일일목표/난이도/시험레벨/언어) 변경 시 today workbook 재호출 ###
      queryClient.invalidateQueries({ queryKey: [EQUERY_KEY.TODAY_CLOZE_WORKBOOK] });
    },
  });
};

// student(studentKey) 출석체크 mutation 함수
export const useCheckStudentAttendanceMutation = () => {
  return useApiMutation<IApiResponse<IAttendanceData>, ICheckAttendanceRequest>(api.checkStudentAttendance, {
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const createdItem = data.data;
      const queryKey = [EQUERY_KEY.ATTENDANCE, variables.attendanceType, variables.studentKey];
      appendQueryDataItem(queryKey, undefined, itemKey, createdItem);
    },
  });
};

export const useCreateStudyRecordMutation = () => {
  return useApiMutation<IApiResponse<void>, ICreateStudyRecordRequest>(
    api.createStudyRecord,
    { mutationKey: [EQUERY_KEY.CREATE_STUDY_RECORD] },
    { dedupInFlight: true },
  );
};

// student(studentKey) 출석 조회 query 함수
export const useAttendanceQuery = (
  studentKey: string,
  attendanceType: EApiAttendanceType,
  options?: { enabled?: boolean },
) => {
  const queryKey = [EQUERY_KEY.ATTENDANCE, attendanceType, studentKey];
  return useApiQuery(
    queryKey,
    api.readAttendancePage,
    { studentKey, attendanceType, page: 1, perPage: 60 },
    {
      enabled: (options?.enabled ?? false) && !!studentKey,
      // staleTime: 60 * 60 * 1000
    },
  );
};

export const useReadMyStudyRecordsQuery = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [EQUERY_KEY.TODAY_VOCABULAIRES, languageCode, studentKey];
  return useApiQuery(
    queryKey,
    api.readStudyRecords,
    { studentKey, languageCode, page: 1, perPage: 500 },
    { enabled: !!studentKey },
  );
};

// ############### vocabulary ###############
// 오늘의 어휘 query 함수 - deprecate
export const useTodayVocabulariesQuery = (languageCode: EApiLanguageCode, count: number) => {
  const queryKey = [EQUERY_KEY.TODAY_VOCABULAIRES];
  return useApiQuery(queryKey, api.getTodayVocabularies, { languageCode, count });
};

// ############### workbook ###############
export const useModifyWorksheetWorkbookMutation = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const syncTodayQueryKey = [EQUERY_KEY.TODAY_WORKSHEET_WORKBOOK, languageCode, studentKey, getTodayDateKey()];

  return useApiMutation<IApiResponse<IWorksheetWorkbookData>, IModifyWorksheetWorkbookRequest>(
    api.modifyWorksheetWorkbook,
    {
      onSuccess: (data) => {
        const itemKey = data.data.key;
        const updatedItem = data.data;
        replaceQueryDataItem(syncTodayQueryKey, undefined, itemKey, updatedItem);
      },
    },
  );
};

export const useWorksheetWorkbookUploadMutation = (workbookKey: string) => {
  return useApiMutation((request: { studentKey: string; format: TAudioFormat }) =>
    api.createWorksheetWorkbookUpload(workbookKey, request),
  );
};

export const useCheckWorksheetWorkbookUploadMutation = (workbookKey: string) => {
  return useApiMutation((request: { studentKey: string }) => api.checkWorksheetWorkbookUpload(workbookKey, request));
};

export const useWorksheetWorkbookTranscribeMutation = (workbookKey: string) => {
  return useApiMutation((request: { studentKey: string }) => api.createWorksheetWorkbookTranscribe(workbookKey, request));
};

export const useTodayWorksheetWorkbookQuery = (languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [EQUERY_KEY.TODAY_WORKSHEET_WORKBOOK, languageCode, studentKey, getTodayDateKey()];
  return useApiQuery(queryKey, api.getTodayWorksheetWorkbook, { languageCode, studentKey }, { enabled: !!studentKey });
};

export const useTodayClozeWorkbookQuery = (
  clozeType: TClozeType,
  languageCode: EApiLanguageCode,
  // workbookCount: number,
) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.TODAY_CLOZE_WORKBOOK, clozeType, languageCode, studentKey, getTodayDateKey()];
  return useApiQuery(
    queryKey,
    api.getTodayClozeWorkbook,
    { clozeType, languageCode, studentKey },
    { enabled: !!studentKey },
  );
};

export const useTodayTestWorkbookQuery = (testCode: TTestCode) => {
  const queryKey = [EQUERY_KEY.TODAY_TEST_WORKBOOK, testCode];
  return useApiQuery(queryKey, api.postTodayTestWorkbook, { testCode });
};

export const useBookmarkClozeWorkbookQuery = (
  clozeType: TClozeType,
  languageCode: EApiLanguageCode,
  isBookmarkRepeat?: boolean,
) => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [
    EQUERY_KEY.BOOKMARK_CLOZE_WORKBOOK,
    clozeType,
    languageCode,
    isBookmarkRepeat ? 'repeat' : 'all',
    studentKey,
  ];
  return useApiInfiniteQuery<
    IWorkbookData,
    {
      clozeType: TClozeType;
      languageCode: EApiLanguageCode;
      page: number;
      perPage: number;
      isBookmarkRepeat?: boolean;
    }
  >(
    queryKey,
    api.getBookmarkClozeWorkbook,
    { clozeType, languageCode, perPage: INFINITE_SCROLL_PER_PAGE, isBookmarkRepeat },
    { enabled: isAuthenticated },
  );
};

export const useIncorrectNoteClozeWorkbookQuery = (
  clozeType: TClozeType,
  languageCode: EApiLanguageCode,
  isIncorrectRepeat?: boolean,
) => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [
    EQUERY_KEY.INCORRECT_NOTE_CLOZE_WORKBOOK,
    clozeType,
    languageCode,
    isIncorrectRepeat ? 'repeat' : 'all',
    studentKey,
  ];
  return useApiInfiniteQuery<
    IWorkbookData,
    {
      clozeType: TClozeType;
      languageCode: EApiLanguageCode;
      page: number;
      perPage: number;
      isIncorrectRepeat?: boolean;
    }
  >(
    queryKey,
    api.getIncorrectNoteClozeWorkbook,
    { clozeType, languageCode, perPage: INFINITE_SCROLL_PER_PAGE, isIncorrectRepeat },
    { enabled: isAuthenticated },
  );
};

export const useModifyClozeWorkbookMutation = (clozeType: TClozeType, languageCode: EApiLanguageCode) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;

  const syncTodayQueryKey = [EQUERY_KEY.TODAY_CLOZE_WORKBOOK, clozeType, languageCode, studentKey, getTodayDateKey()];
  const syncInfiniteQueryKeys = [
    [EQUERY_KEY.BOOKMARK_CLOZE_WORKBOOK, clozeType, languageCode, 'all', studentKey],
    [EQUERY_KEY.BOOKMARK_CLOZE_WORKBOOK, clozeType, languageCode, 'repeat', studentKey],
    [EQUERY_KEY.INCORRECT_NOTE_CLOZE_WORKBOOK, clozeType, languageCode, 'all', studentKey],
    [EQUERY_KEY.INCORRECT_NOTE_CLOZE_WORKBOOK, clozeType, languageCode, 'repeat', studentKey],
  ];
  const allSyncQueryKeys = [syncTodayQueryKey, ...syncInfiniteQueryKeys];

  return useApiMutation<
    IApiResponse<IWorkbookData>,
    IModifyWorkbookRequest,
    { snapshots: { queryKey: any; data: unknown }[] }
  >(api.modifyWorkbook, {
    onMutate: (variables) => {
      const snapshots = snapshotQueryData(allSyncQueryKeys);

      // 낙관적 업데이트 적용 필드
      const patch: Partial<IWorkbookData> = {};
      if (variables.isBookmark !== undefined) patch.isBookmark = variables.isBookmark;
      if (variables.isBookmarkRepeat !== undefined) patch.isBookmarkRepeat = variables.isBookmarkRepeat;
      if (variables.isIncorrectRepeat !== undefined) patch.isIncorrectRepeat = variables.isIncorrectRepeat;
      if (variables.isCorrect !== undefined) patch.isCorrect = variables.isCorrect;

      if (Object.keys(patch).length > 0) {
        mergeQueryDataItem<IWorkbookData>(syncTodayQueryKey, variables.workbookKey, patch);
        syncInfiniteQueryKeys.forEach((queryKey) => {
          mergeInfiniteQueryDataItem<IWorkbookData>(queryKey, variables.workbookKey, patch);
        });
      }

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackQueryData(context.snapshots);
    },
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem(syncTodayQueryKey, undefined, itemKey, updatedItem);
      syncInfiniteQueryKeys.forEach((queryKey) => {
        replaceInfiniteQueryDataItem(queryKey, itemKey, updatedItem);
      });
    },
  });
};

export const useModifyTestWorkbookMutation = (testCode: TTestCode) => {
  return useApiMutation<IApiResponse<IWorkbookData>, IModifyWorkbookRequest>(api.modifyWorkbook, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([EQUERY_KEY.TODAY_TEST_WORKBOOK, testCode], undefined, itemKey, updatedItem);
    },
  });
};

// ############### authentic expression ###############
// 생생표현 목록 query — clip-home의 "오늘 1개"와 toast의 "지난 표현 무한스크롤"이 같은 캐시 공유
export const useExpressionsQuery = (languageCode: EApiLanguageCode, releaseDate: string) => {
  const queryKey = [EQUERY_KEY.EXPRESSIONS, languageCode, releaseDate];
  return useApiInfiniteQuery<
    IAuthenticExpressionData,
    { page: number; perPage: number; languageCode: EApiLanguageCode; releaseDate: string }
  >(queryKey, api.getExpressions, { perPage: INFINITE_SCROLL_PER_PAGE, languageCode, releaseDate });
};

// ############### story ###############
// 이야기 목록 query — clip-home의 "오늘 1개"와 toast의 "지난 이야기 무한스크롤"이 같은 캐시 공유
export const useStoriesQuery = (languageCode: EApiLanguageCode, storyType: EApiStoryType, releaseDate: string) => {
  const queryKey = [EQUERY_KEY.STORIES, languageCode, storyType, releaseDate];
  return useApiInfiniteQuery<
    IStoryData,
    { page: number; perPage: number; languageCode: EApiLanguageCode; storyType: EApiStoryType; releaseDate: string }
  >(queryKey, api.getStories, { perPage: INFINITE_SCROLL_PER_PAGE, languageCode, storyType, releaseDate });
};

// ############### vocabulary book (me) ###############
export const useVocabularyBooksQuery = (request: IReadVocabularyBooksRequest, options?: { enabled?: boolean }) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [
    EQUERY_KEY.MY_VOCABULARY_BOOKS,
    request.languageCode,
    request.category ?? 'all',
    request.levels?.join(',') ?? 'all',
    request.page,
    request.perPage,
    studentKey,
  ];
  return useApiQuery(
    queryKey,
    api.getVocabularyBooks,
    { ...request, studentKey },
    { enabled: (options?.enabled ?? true) && !!studentKey },
  );
};

export const useVocabularyBookProgressQuery = (
  request: IReadVocabularyBookProgressRequest,
  options?: { enabled?: boolean },
) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [
    EQUERY_KEY.MY_VOCABULARY_BOOK_PROGRESS,
    request.languageCode,
    request.category,
    request.level,
    studentKey,
  ];
  return useApiQuery(
    queryKey,
    api.getVocabularyBookProgress,
    { ...request, studentKey },
    { enabled: (options?.enabled ?? true) && !!studentKey },
  );
};

export const useVocabularyWorkbookBookQuery = (
  vocabularyBookKey: string | null | undefined,
  options?: { enabled?: boolean },
) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
  const queryKey = [EQUERY_KEY.VOCABULARY_WORKBOOK_BOOK, vocabularyBookKey, studentKey];
  return useApiQuery(
    queryKey,
    api.getVocabularyWorkbookBook,
    { vocabularyBookKey: vocabularyBookKey ?? '', studentKey },
    { enabled: !!vocabularyBookKey && !!studentKey && (options?.enabled ?? true) },
  );
};

export const useVocabularyWorkbookBookmarkPageQuery = (languageCode: EApiLanguageCode, isBookmarkRepeat?: boolean) => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [
    EQUERY_KEY.BOOKMARK_VOCABULARY_WORKBOOK,
    languageCode,
    isBookmarkRepeat ? 'repeat' : 'all',
    studentKey,
  ];
  return useApiInfiniteQuery<
    IVocabularyWorkbookData,
    {
      languageCode: EApiLanguageCode;
      page: number;
      perPage: number;
      isBookmarkRepeat?: boolean;
    }
  >(
    queryKey,
    api.getVocabularyWorkbookBookmarkPage,
    { languageCode, perPage: INFINITE_SCROLL_PER_PAGE, isBookmarkRepeat },
    { enabled: isAuthenticated },
  );
};

export const useVocabularyWorkbookIncorrectNotePageQuery = (
  languageCode: EApiLanguageCode,
  isIncorrectRepeat?: boolean,
) => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [
    EQUERY_KEY.INCORRECT_NOTE_VOCABULARY_WORKBOOK,
    languageCode,
    isIncorrectRepeat ? 'repeat' : 'all',
    studentKey,
  ];
  return useApiInfiniteQuery<
    IVocabularyWorkbookData,
    {
      languageCode: EApiLanguageCode;
      page: number;
      perPage: number;
      isIncorrectRepeat?: boolean;
    }
  >(
    queryKey,
    api.getVocabularyWorkbookIncorrectNotePage,
    { languageCode, perPage: INFINITE_SCROLL_PER_PAGE, isIncorrectRepeat },
    { enabled: isAuthenticated },
  );
};

// 재학습(기억 다지기) 큐 워크북 — 페이징 (cloze 노트 패턴 미러)
export const useReviewVocabularyWorkbookPageQuery = (
  languageCode: EApiLanguageCode,
  category: EApiVocabularyBookCategory,
  level: EApiVocabularyBookLevel | null | undefined,
) => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const queryKey = [EQUERY_KEY.REVIEW_VOCABULARY_WORKBOOK, languageCode, category, level, studentKey];
  return useApiInfiniteQuery<
    IVocabularyWorkbookData,
    {
      languageCode: EApiLanguageCode;
      category: EApiVocabularyBookCategory;
      level: EApiVocabularyBookLevel;
      page: number;
      perPage: number;
    }
  >(
    queryKey,
    api.getReviewVocabularyWorkbookPage,
    {
      languageCode,
      category,
      level: level as EApiVocabularyBookLevel,
      perPage: INFINITE_SCROLL_PER_PAGE,
    },
    { enabled: isAuthenticated && !!level },
  );
};

export const useModifyVocabularyWorkbookMutation = (
  languageCode: EApiLanguageCode,
  vocabularyBookKey: string | null | undefined,
  category: EApiVocabularyBookCategory | null | undefined,
  level: EApiVocabularyBookLevel | null | undefined,
) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;

  const syncBookQueryKey = [EQUERY_KEY.VOCABULARY_WORKBOOK_BOOK, vocabularyBookKey, studentKey];
  const syncInfiniteQueryKeys = [
    [EQUERY_KEY.BOOKMARK_VOCABULARY_WORKBOOK, languageCode, 'all', studentKey],
    [EQUERY_KEY.BOOKMARK_VOCABULARY_WORKBOOK, languageCode, 'repeat', studentKey],
    [EQUERY_KEY.INCORRECT_NOTE_VOCABULARY_WORKBOOK, languageCode, 'all', studentKey],
    [EQUERY_KEY.INCORRECT_NOTE_VOCABULARY_WORKBOOK, languageCode, 'repeat', studentKey],
    ...(category && level ? [[EQUERY_KEY.REVIEW_VOCABULARY_WORKBOOK, languageCode, category, level, studentKey]] : []),
  ];
  const allSyncQueryKeys = [syncBookQueryKey, ...syncInfiniteQueryKeys];

  return useApiMutation<
    IApiResponse<IVocabularyWorkbookData>,
    IModifyVocabularyWorkbookRequest,
    { snapshots: { queryKey: any; data: unknown }[] }
  >(api.modifyVocabularyWorkbook, {
    onMutate: (variables) => {
      const snapshots = snapshotQueryData(allSyncQueryKeys);

      // 낙관적 업데이트 적용 필드
      const patch: Partial<IVocabularyWorkbookData> = {};
      if (variables.isBookmark !== undefined) patch.isBookmark = variables.isBookmark;
      if (variables.isBookmarkRepeat !== undefined) patch.isBookmarkRepeat = variables.isBookmarkRepeat;
      if (variables.isIncorrectRepeat !== undefined) patch.isIncorrectRepeat = variables.isIncorrectRepeat;
      if (variables.isIncorrectNote !== undefined) {
        patch.isIncorrectNote = variables.isIncorrectNote;
        // 백엔드 unsetIncorrectNote가 isIncorrectRepeat도 false로 만들기 때문에 캐시도 동기화
        if (variables.isIncorrectNote === false) patch.isIncorrectRepeat = false;
      }
      if (variables.grade !== undefined) {
        patch.grade = variables.grade;
        patch.studiedAt = new Date().toISOString();
      }

      if (Object.keys(patch).length > 0) {
        mergeQueryDataItem<IVocabularyWorkbookData>(syncBookQueryKey, variables.workbookKey, patch);
        syncInfiniteQueryKeys.forEach((queryKey) => {
          mergeInfiniteQueryDataItem<IVocabularyWorkbookData>(queryKey, variables.workbookKey, patch);
        });
      }

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackQueryData(context.snapshots);
    },
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem(syncBookQueryKey, undefined, itemKey, updatedItem);
      syncInfiniteQueryKeys.forEach((queryKey) => {
        replaceInfiniteQueryDataItem(queryKey, itemKey, updatedItem);
      });
    },
  });
};

export const useDailyStudyRecordsQuery = (languageCode: EApiLanguageCode, startDate: string, endDate: string) => {
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  return useApiQuery(
    [EQUERY_KEY.MY_STUDY_RECORDS, studentKey, languageCode, startDate, endDate],
    api.readDailyStudyRecords, { studentKey, languageCode, startDate, endDate },
    { enabled: !!studentKey },
  );
};
